import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation } from '@apollo/client/react';
import { useLocation } from 'react-router';
import { USER_TALKED_WITH_YOGI } from '../../../graphql/mutations/index.js';
import { FEATURES } from '../../../config/features.js';
import { STORAGE_KEYS } from '../../../config/env.js';
import { useAuthStore } from '../../../core/stores/authStore.js';
import { useYogiChat } from '../hooks/useYogiChat.js';
import { YogiChatPanel } from './YogiChatPanel.jsx';

function playYogiSound() {
  const soundOn = localStorage.getItem(STORAGE_KEYS.soundOn);
  if (soundOn !== 'true') return;
  const audio = new Audio('/sounds/yogiSound.mp3');
  audio.load();
  audio.play().catch(() => {});
}

export function YogiBubble() {
  const location = useLocation();
  const currentUser = useAuthStore((s) => s.currentUser);
  const setUser = useAuthStore((s) => s.setUser);

  const [isOpen, setIsOpen] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [hasMarkedTalked, setHasMarkedTalked] = useState(false);
  const introPlayed = useRef(false);

  const [talkedWithYogi] = useMutation(USER_TALKED_WITH_YOGI);

  const isFeedTab = location.pathname === '/';

  const chat = useYogiChat(isOpen ? currentUser : null);

  const markTalkedWithYogi = useCallback(async () => {
    if (!currentUser?.id || currentUser.talkedWithYogi || hasMarkedTalked) return;

    try {
      const { data } = await talkedWithYogi({ variables: { userId: currentUser.id } });
      if (data?.userTalkedWithYogi) {
        setUser({ ...currentUser, talkedWithYogi: data.userTalkedWithYogi.talkedWithYogi });
      }
      setHasMarkedTalked(true);
    } catch {
      setHasMarkedTalked(true);
    }
  }, [currentUser, hasMarkedTalked, setUser, talkedWithYogi]);

  useEffect(() => {
    if (!FEATURES.yogiChatbot || !isFeedTab || !currentUser) return;
    if (currentUser.talkedWithYogi || hasMarkedTalked) return;

    setShowIntro(true);
    if (!introPlayed.current) {
      introPlayed.current = true;
      playYogiSound();
    }

    const timer = setTimeout(() => setShowIntro(false), 3000);
    return () => clearTimeout(timer);
  }, [currentUser, hasMarkedTalked, isFeedTab]);

  const handleOpen = () => {
    setIsOpen(true);
    setShowIntro(false);
    markTalkedWithYogi();
  };

  const handleClose = () => {
    setIsOpen(false);
    chat.reset();
  };

  if (!FEATURES.yogiChatbot || !isFeedTab) return null;

  return (
    <>
      {showIntro && !isOpen && !currentUser?.talkedWithYogi && !hasMarkedTalked && (
        <div className="fixed right-4 top-32 z-40 flex h-36 w-72 items-center justify-center bg-[url('/images/yogiBlob.png')] bg-contain bg-center bg-no-repeat p-4 animate-[fade-in_0.4s_ease-out] md:top-36 md:h-40 md:w-80">
          <p className="max-w-[160px] text-center text-sm leading-tight text-brand md:max-w-[180px] md:text-base">
            Hi, I&apos;m your personal Yogi. Click on me to get started
          </p>
        </div>
      )}

      {isOpen && currentUser && (
        <YogiChatPanel
          messages={chat.messages}
          pendingOptions={chat.pendingOptions}
          activeComponent={chat.activeComponent}
          context={chat.context}
          profilePictureUrl={currentUser.profilePictureUrl}
          userId={currentUser.id}
          userName={currentUser.name}
          phoneNumber={currentUser.phoneNumber}
          onOptionSelect={chat.handleOptionSelect}
          onComponentComplete={chat.handleComponentComplete}
          onClose={handleClose}
        />
      )}

      {!isOpen && (
        <button
          type="button"
          onClick={handleOpen}
          className="yogi-pulse group fixed right-4 top-32 z-30 h-32 w-32 min-h-0 min-w-0 overflow-hidden rounded-full transition-transform hover:scale-110 active:scale-95 md:top-36 md:h-44 md:w-44"
          aria-label="Open Yogi assistant"
        >
          <img
            src="/images/Yogi.png"
            alt="Yogi"
            className="yogi-float h-full w-full object-contain group-hover:scale-105"
          />
        </button>
      )}
    </>
  );
}
